import type { Action, Problem, z } from '@mkcore/shared';

/** An error answer of the API as an RFC 9457 problem (docs/01-architectuur/05-api-en-mcp.md). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: Partial<Problem>;

  constructor(status: number, problem: Partial<Problem>) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = problem.code ?? String(status);
    this.problem = problem;
  }

  /** Field errors by field name (from the JSON pointers), to show at the right field. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) {
      const field = e.pointer.replace(/^\//, '').split('/')[0];
      if (field && !out[field]) out[field] = e.message;
    }
    return out;
  }

  static async from(res: Response): Promise<ApiError> {
    let body: Partial<Problem>;
    try {
      body = (await res.json()) as Partial<Problem>;
    } catch {
      body = { title: res.statusText };
    }
    return new ApiError(res.status, body);
  }
}

export interface CallOptions {
  /** One key per user action; a retry of the same action reuses it (docs/01-architectuur/02-principes.md, R3). */
  idempotencyKey?: string;
  /** The version the user edited; sent as If-Match. */
  ifMatch?: number;
  signal?: AbortSignal;
}

/**
 * Typed fetch on an action of the shared schema (docs/01-architectuur/11-frontend.md, Data-laag): the same
 * input and output types as the REST route and the MCP tool. No generated client.
 */
export async function call<I extends z.ZodTypeAny, O extends z.ZodTypeAny>(
  action: Action<I, O>,
  input: z.input<I>,
  opts: CallOptions = {},
): Promise<z.output<O>> {
  const hasBody = action.method !== 'get' && action.method !== 'delete';
  const body: Record<string, unknown> = { ...(input as Record<string, unknown>) };
  for (const p of action.pathParams) delete body[p];
  delete body.expectedVersion;
  const res = await fetch(`/api/v1${action.pathFor(input)}`, {
    method: action.method.toUpperCase(),
    headers: {
      accept: 'application/json',
      ...(hasBody && { 'content-type': 'application/json' }),
      ...(opts.idempotencyKey && { 'idempotency-key': opts.idempotencyKey }),
      ...(opts.ifMatch !== undefined && { 'if-match': `"${opts.ifMatch}"` }),
    },
    body: hasBody ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
    signal: opts.signal,
  });
  if (!res.ok) throw await ApiError.from(res);
  if (res.status === 204) return undefined as z.output<O>;
  const data: unknown = await res.json();
  // In development the answer is checked against the contract, so drift between server and schema shows at once.
  return import.meta.env.DEV ? action.output.parse(data) : (data as z.output<O>);
}

/** A key per user action: created when the form opens or the button is pressed, reused on retry. */
export const newIdempotencyKey = () => crypto.randomUUID();
